// ══════════════════════════════════════════════════════════════
//  COMPANY SETTINGS
// ══════════════════════════════════════════════════════════════
export const COMPANY_SETTINGS = {
  companyName: "GoSnel",
  companyLogo: "https://media.gosnel.com/GoSnelLogoText.png",
  primaryColor: "#4ecdc4",
  countryCode: "971", // UAE country code
  whatsappMsg: "مرحباً، أنا من فريق GoSnel ونودّ التحدث معكم بخصوص الانضمام لمنصتنا 🙂",
};

// ══════════════════════════════════════════════════════════════
//  STATUS METADATA
// ══════════════════════════════════════════════════════════════
export const STATUS_META = {
  new:             { label:"جديد",                    color:"#64748b", bg:"#f1f5f9",  icon:"Sparkles", order:0 },
  data_review:     { label:"مراجعة البيانات",         color:"#f97316", bg:"#ffedd5",  icon:"FileEdit", order:1 },
  ready_for_rep:   { label:"مراجعة المدير",           color:"#3b82f6", bg:"#dbeafe",  icon:"Search", order:2 },
  invoice_pending: { label:"بانتظار الفاتورة",        color:"#7c3aed", bg:"#ede9fe",  icon:"Banknote", order:3 },
  in_field:        { label:"في الميدان",              color:"#4ecdc4", bg:"#f0fdfa",  icon:"Walking", order:4 },
  pending_closure: { label:"بانتظار الإغلاق",         color:"#f97316", bg:"#ffedd5",  icon:"Hourglass", order:5 },
  deal_closed:     { label:"تم الإغلاق",              color:"#10b981", bg:"#d1fae5",  icon:"CheckCircle", order:6 },
};

// ══════════════════════════════════════════════════════════════
//  ROLE METADATA
// ══════════════════════════════════════════════════════════════
export const ROLE_META = {
  admin:         { label:"المدير التنفيذي", color:"#f59e0b", icon:"Crown" },
  marketer:      { label:"منسّقة صفقات",   color:"#10b981", icon:"Megaphone" },
  data_entry:    { label:"مدخل بيانات",     color:"#0ea5e9", icon:"Keyboard" },
  sales_manager: { label:"مدير مبيعات",     color:"#a78bfa", icon:"Target" },
  accountant:    { label:"محاسب",           color:"#fb923c", icon:"Briefcase" },
  rep:           { label:"مندوب ميداني",    color:"#34d399", icon:"Handshake" },
};

// ══════════════════════════════════════════════════════════════
//  ROLE VISIBILITY (which statuses each role can see)
// ══════════════════════════════════════════════════════════════
export const ROLE_VISIBILITY = {
  admin:         null,
  sales_manager: null,
  marketer:      null, // filtered by assignedMarketer
  data_entry:    ["data_review"],
  accountant:    ["invoice_pending"],
  rep:           ["in_field", "pending_closure"],
};

// ══════════════════════════════════════════════════════════════
//  FILE UPLOAD PERMISSIONS PER ROLE
// ══════════════════════════════════════════════════════════════
export const ROLE_UPLOAD_PERMS = {
  marketer:      ["logoUrl", "menu1Url", "menu2Url"],
  data_entry:    [],
  sales_manager: [],
  accountant:    ["invoiceUrl"],
  rep:           ["tradeLicenseUrl", "ownerIdUrl"],
  admin:         ["logoUrl", "menu1Url", "menu2Url", "invoiceUrl", "tradeLicenseUrl", "ownerIdUrl"],
};

// ══════════════════════════════════════════════════════════════
//  CSV COLUMN HINTS
// ══════════════════════════════════════════════════════════════
export const CSV_HINTS = {
  name:["name","restaurant_name","restaurant","اسم المطعم","المطعم"],
  ownerName:["owner","owner_name","صاحب","مالك"],
  phone:["phone","mobile","tel","هاتف","جوال","رقم"],
  area:["district","area","region","location","المنطقة","الحي"],
  mapsUrl:["maps","google_maps","map_url","خريطة","موقع"],
  notes:["notes","remarks","ملاحظات"],
};

// ══════════════════════════════════════════════════════════════
//  GLOBAL STYLES (LIGHT MODE)
// ══════════════════════════════════════════════════════════════
export const S = {
  bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
  txt: "#0f172a", muted: "#64748b", input: "#ffffff",
  pri: "#4ecdc4", ok: "#10b981", warn: "#f97316", err: "#ef4444",
};

// ══════════════════════════════════════════════════════════════
//  UTILITY FUNCTIONS
// ══════════════════════════════════════════════════════════════

// Check if all required files are present
export function allFilesPresent(files) {
  return !!(files.logoUrl && files.menu1Url && files.invoiceUrl && files.tradeLicenseUrl && files.ownerIdUrl);
}

// Format phone number
export function fmtPhone(raw, cc = "971") {
  const d = raw.replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("00")) return "+" + d.slice(2);
  if (d.startsWith("0"))  return "+" + cc + d.slice(1);
  return "+" + d;
}

// Detect CSV column mapping
export function detectCol(h) {
  const lh = h.toLowerCase().trim();
  for (const [f, hints] of Object.entries(CSV_HINTS)) {
    if (hints.some(hint => lh.includes(hint.toLowerCase()))) return f;
  }
  return "ignore";
}

// Parse CSV text
export function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers:[], rows:[] };
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g,""));
  const rows = lines.slice(1).map(line => {
    const vals=[]; let cur="", inQ=false;
    for (const ch of line) {
      if (ch==='"') inQ=!inQ;
      else if (ch===','&&!inQ) { vals.push(cur.trim()); cur=""; }
      else cur+=ch;
    }
    vals.push(cur.trim());
    return headers.reduce((o,h,i) => { o[h]=vals[i]||""; return o; }, {});
  });
  return { headers, rows };
}

// Create file object with defaults
export const mkF = (o={}) => ({ 
  logo_url:"", menu_url_1:"", menu_url_2:"", invoice_url:"", 
  trade_license_url:"", owner_id_url:"", ...o 
});

// Format time ago (for presence tracking)
export function timeAgo(date) {
  if (!date) return 'غير معروف';
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'الآن';
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
  if (diffHours < 24) return `منذ ${diffHours} ساعة`;
  if (diffDays < 7) return `منذ ${diffDays} يوم`;
  return past.toLocaleDateString('ar-AE');
}

// Check if user is online (active within last 5 minutes)
export function isUserOnline(lastActive) {
  if (!lastActive) return false;
  const now = new Date();
  const last = new Date(lastActive);
  return (now - last) < 5 * 60 * 1000; // 5 minutes
}
